import {
  Flex,
  IconButton,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Portal,
  Text,
  Tooltip,
} from '@chakra-ui/react';

import { CellContext, ColumnDef, createColumnHelper } from '@tanstack/react-table';

import { parseISO } from 'date-fns';

import { describeAttention } from 'app/utils/campaigns/attention';
import { CAMPAIGN_TIMEZONE } from 'app/utils/campaigns/schedule';

import {
  Badge,
  BadgeStatus,
  Campaign,
  CampaignStatus,
  CampaignType,
  RefreshOutline,
  WarningOutline,
  MoreCircleOutline,
} from '@aslanpay/portal-common-lib';

/** What each kind of campaign is called. One kind today; named here so a second reads properly on arrival. */
export const campaignTypeLabels: Record<CampaignType, string> = {
  VOTING: 'Voting',
};

export const campaignStatusDetails: Record<
  CampaignStatus,
  { label: string; badgeStatus: BadgeStatus }
> = {
  DRAFT: { label: 'Draft', badgeStatus: 'invited' },
  PENDING: { label: 'Not started', badgeStatus: 'locked' },
  VOTING_OPEN: { label: 'Nominations open', badgeStatus: 'active' },
  VOTING_CLOSED: { label: 'Nominations closed', badgeStatus: 'warning' },
  INTERNAL_REVIEW_OPEN: { label: 'In review', badgeStatus: 'registered' },
  INTERNAL_REVIEW_CLOSED: { label: 'Review closed', badgeStatus: 'warning' },
  COMPLETED: { label: 'Completed', badgeStatus: 'created' },
  FAILED: { label: 'Failed', badgeStatus: 'left' },
  CANCELLED: { label: 'Cancelled', badgeStatus: 'disabled' },
};

/** Where a campaign runs, told by how many prides it fanned out to rather than by the enum. */
export function describeReach(campaign: Campaign) {
  if (campaign.groupSelection === 'NONE') return 'Whole company';

  const prides = campaign.children?.length ?? 0;
  const noun = prides === 1 ? 'pride' : 'prides';

  return campaign.groupSelection === 'ALL' ? `Every pride (${prides})` : `${prides} chosen ${noun}`;
}

const campaignDateTimeFormat = new Intl.DateTimeFormat('en-GB', {
  timeZone: CAMPAIGN_TIMEZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

function campaignDateParts(value: string): Record<string, string> {
  return campaignDateTimeFormat
    .formatToParts(parseISO(value))
    .reduce<Record<string, string>>((parts, part) => ({ ...parts, [part.type]: part.value }), {});
}

export const formatDate = (value: string | null | undefined) => {
  if (!value) {
    return '-';
  }

  const { day, month, year } = campaignDateParts(value);

  return `${day}/${month}/${year}`;
};

/** A moment of the schedule reads to the minute: which day it lands on is half the answer. */
export const formatDateTime = (value: string | null | undefined) => {
  if (!value) {
    return '-';
  }

  const { day, month, year, hour, minute } = campaignDateParts(value);

  return `${day}/${month}/${year} ${hour}:${minute}`;
};

/** What a campaign row can be acted on with. Each is offered only where the service would accept it. */
export interface CampaignActions {
  view: (campaign: Campaign) => void;
  edit: (campaign: Campaign) => void;
  publish: (campaign: Campaign) => void;
  unpublish: (campaign: Campaign) => void;
  cancel: (campaign: Campaign) => void;
}

/** A campaign that has run its course has nothing left to stop. */
const FINISHED: CampaignStatus[] = ['COMPLETED', 'CANCELLED', 'FAILED'];

function getCampaignColumns(onSelect: (campaign: Campaign) => void, actions: CampaignActions) {
  const columnHelper = createColumnHelper<Campaign>();

  const rowClick = {
    getCellContext: (context: CellContext<Campaign, unknown>) => ({
      onClick: () => onSelect(context.row.original),
    }),
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const columns: ColumnDef<Campaign, any>[] = [
    columnHelper.accessor('name', {
      header: 'Name',
      enableSorting: false,
      cell: ({ row, getValue }) => {
        const attention = describeAttention(row.original.attentionReasons);

        return (
          <Flex align="center" gap="6px">
            {row.original.recurring && (
              <Tooltip label="This campaign runs again on a schedule" hasArrow>
                <span
                  aria-label="Runs again on a schedule"
                  data-testid={`campaign-recurring-${row.original.id}`}
                >
                  <RefreshOutline boxSize="14px" color="system.success" />
                </span>
              </Tooltip>
            )}
            {attention.length > 0 && (
              <Tooltip
                hasArrow
                label={
                  <Flex flexDir="column" gap="2px">
                    {attention.map((reason) => (
                      <Text key={reason} textStyle="extraSmallRegular">
                        {reason}
                      </Text>
                    ))}
                  </Flex>
                }
              >
                <span
                  aria-label={`Needs attention: ${attention.join(' ')}`}
                  data-testid={`campaign-attention-${row.original.id}`}
                >
                  <WarningOutline boxSize="14px" color="system.warning" />
                </span>
              </Tooltip>
            )}
            <Text textStyle="smallMedium">{getValue() as string}</Text>
          </Flex>
        );
      },
      meta: rowClick,
    }),
    columnHelper.accessor('campaignType', {
      header: 'Type',
      enableSorting: false,
      cell: ({ getValue }) => campaignTypeLabels[getValue() as CampaignType] ?? getValue(),
      meta: rowClick,
    }),
    columnHelper.display({
      id: 'reach',
      header: 'Where it runs',
      cell: ({ row }) => describeReach(row.original),
      meta: rowClick,
    }),
    columnHelper.accessor('status', {
      header: 'Status',
      enableSorting: false,
      cell: ({ getValue }) => {
        const details = campaignStatusDetails[getValue() as CampaignStatus];

        return <Badge value={details.label} status={details.badgeStatus} />;
      },
      meta: { ...rowClick, customColumnStyles: { width: '1%', whiteSpace: 'nowrap' } },
    }),
    columnHelper.accessor('awardPoints', {
      header: 'Award',
      enableSorting: false,
      cell: ({ getValue }) => {
        const points = getValue() as number | null | undefined;

        return points === null || points === undefined
          ? '-'
          : `${points.toLocaleString('en-GB')} pts`;
      },
    }),
    columnHelper.accessor('nominationsOpenAt', {
      header: 'Nominations open',
      enableSorting: false,
      cell: ({ getValue }) => formatDateTime(getValue() as string),
      meta: rowClick,
    }),
    columnHelper.accessor('nominationsCloseAt', {
      header: 'Nominations close',
      enableSorting: false,
      cell: ({ getValue }) => formatDateTime(getValue() as string),
      meta: rowClick,
    }),
    columnHelper.accessor('reviewOpensAt', {
      header: 'Review opens',
      enableSorting: false,
      cell: ({ getValue }) => formatDateTime(getValue() as string),
      meta: rowClick,
    }),
    columnHelper.accessor('reviewClosesAt', {
      header: 'Review closes',
      enableSorting: false,
      cell: ({ getValue }) => formatDateTime(getValue() as string),
      meta: rowClick,
    }),
    columnHelper.accessor('announcementAt', {
      header: 'Winner announced',
      enableSorting: false,
      cell: ({ getValue }) => formatDateTime(getValue() as string),
      meta: rowClick,
    }),
    columnHelper.display({
      id: 'options',
      header: '',
      // No row click: this cell acts on the campaign rather than leading to it.
      meta: {
        getCellContext: () => ({ style: { padding: 0, textAlign: 'end' } }),
        customStyles: { justifyContent: 'center' },
        pin: 'right',
      },
      cell: ({ row }) => {
        const campaign = row.original;
        const isDraft = campaign.status === 'DRAFT';
        // Publishing hands a campaign to the lifecycle and nothing has happened until it opens, so up to that
        // point the handover can be taken back. One that has opened is cancelled instead.
        const isPending = campaign.status === 'PENDING';
        const hasFinished = FINISHED.includes(campaign.status);

        return (
          <Menu autoSelect={false} strategy="fixed">
            <MenuButton
              as={IconButton}
              aria-label={`Options for ${campaign.name}`}
              icon={<MoreCircleOutline boxSize="20px" />}
              variant="menu"
              data-testid={`campaign-actions-${campaign.id}`}
            />
            <Portal>
              <MenuList>
                <MenuItem
                  onClick={() => actions.view(campaign)}
                  data-testid={`view-campaign-${campaign.id}`}
                >
                  View
                </MenuItem>
                <MenuItem
                  onClick={() => actions.edit(campaign)}
                  data-testid={`edit-campaign-${campaign.id}`}
                >
                  Edit
                </MenuItem>
                {!isPending && (
                  <MenuItem
                    isDisabled={!isDraft}
                    onClick={() => actions.publish(campaign)}
                    data-testid={`publish-campaign-${campaign.id}`}
                  >
                    {isDraft ? 'Publish' : 'Already published'}
                  </MenuItem>
                )}
                {isPending && (
                  <MenuItem
                    onClick={() => actions.unpublish(campaign)}
                    data-testid={`unpublish-campaign-${campaign.id}`}
                  >
                    Put back to draft
                  </MenuItem>
                )}
                <MenuDivider />
                <MenuItem
                  isDisabled={hasFinished || isDraft}
                  color="text.error"
                  onClick={() => actions.cancel(campaign)}
                  data-testid={`cancel-campaign-${campaign.id}`}
                >
                  {isDraft ? 'Nothing to cancel yet' : 'Cancel'}
                </MenuItem>
              </MenuList>
            </Portal>
          </Menu>
        );
      },
    }),
  ];

  return columns;
}

export default getCampaignColumns;
